import { useEffect } from "react";
import { SITE_URL, DEFAULT_OG_IMAGE, ORG_NAME } from "@/lib/seo";

function upsertMeta(attr, key, content) {
  if (content == null || content === "") return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * Client-side head manager (React 19 safe, dependency-free). Sets the title,
 * meta description, canonical, robots, Open Graph + Twitter tags, and an
 * optional per-page JSON-LD block. Googlebot renders JS, so these are indexed.
 */
export function Seo({
  title,
  description,
  path,
  image,
  type = "website",
  jsonLd = null,
  noindex = false,
}) {
  const jsonLdStr = jsonLd ? JSON.stringify(jsonLd) : "";

  useEffect(() => {
    const url =
      SITE_URL + (path != null ? path : window.location.pathname);
    const ogImage = image || DEFAULT_OG_IMAGE;

    if (title) document.title = title;
    upsertMeta("name", "description", description);
    upsertLink("canonical", url);
    upsertMeta("name", "robots", noindex ? "noindex,nofollow" : "index,follow");

    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:image", ogImage);
    upsertMeta("property", "og:site_name", ORG_NAME);

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", ogImage);

    // Per-page JSON-LD lives in a tag we own and replace on each render.
    const ID = "seo-jsonld";
    let script = document.getElementById(ID);
    if (jsonLdStr) {
      if (!script) {
        script = document.createElement("script");
        script.type = "application/ld+json";
        script.id = ID;
        document.head.appendChild(script);
      }
      script.textContent = jsonLdStr;
    } else if (script) {
      script.remove();
    }
  }, [title, description, path, image, type, noindex, jsonLdStr]);

  return null;
}
